import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Square, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface FrameAnalysis {
  id: string;
  timestamp: number;
  imageUrl: string;
  status: 'pending' | 'analyzing' | 'complete' | 'error';
  analysis?: {
    damageDetected: boolean;
    predictions: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      confidence: number;
      class: string;
      bounding_box?: number[];
    }>;
    findings: string[];
    summary?: {
      totalFindings: number;
      hailCount: number;
      otherDamageCount: number;
    };
  };
  error?: string;
}

interface PinMarker {
  id: string;
  x: number;
  y: number;
  label: string;
  color: string;
  aiPredictionIndex?: number;
}

interface VideoFrameDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  frame: FrameAnalysis | null;
  onAddToProposal: (frame: FrameAnalysis, pins?: PinMarker[]) => void;
}

const DAMAGE_COLORS: Record<string, string> = {
  "hail-damage": "#f97316",
  "wind-damage": "#eab308",
  "missing-shingles": "#ef4444",
  "nail-pops": "#8b5cf6",
  "nail-pop": "#8b5cf6",
  "other": "#6b7280",
};

const DAMAGE_TYPES = [
  { value: "missing-shingles", label: "Missing Shingles", color: "#ef4444" },
  { value: "hail-damage", label: "Hail Damage", color: "#f97316" },
  { value: "wind-damage", label: "Wind Damage", color: "#eab308" },
  { value: "nail-pop", label: "Nail Pops", color: "#8b5cf6" },
  { value: "water-damage", label: "Water Damage", color: "#3b82f6" },
  { value: "cracked-shingles", label: "Cracked Shingles", color: "#a855f7" },
  { value: "other", label: "Other Damage", color: "#6b7280" },
];

export const VideoFrameDetailDialog = ({ open, onOpenChange, frame, onAddToProposal }: VideoFrameDetailDialogProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [canvasHeight, setCanvasHeight] = useState(0);
  
  // Pin marker state
  const [pins, setPins] = useState<PinMarker[]>([]);
  const [selectedLabel, setSelectedLabel] = useState(DAMAGE_TYPES[0].label);
  const [pinMode, setPinMode] = useState(false);
  const [placingForAI, setPlacingForAI] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setPins([]);
      setPinMode(false);
      setPlacingForAI(null);
      setImageLoaded(false);
    }
  }, [open, frame]);
 
  useEffect(() => {
    if (imageLoaded) {
      drawPinMarkers();
    }
  }, [imageLoaded, pins]);

  const handleImageLoad = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    setCanvasWidth(displayWidth);
    setCanvasHeight(displayHeight);
    canvas.width = displayWidth;
    canvas.height = displayHeight;

    setScaleX(displayWidth / img.naturalWidth);
    setScaleY(displayHeight / img.naturalHeight);

    setImageLoaded(true);
  };

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!pinMode) return;
    const pos = getMousePos(e);
    
    const newPin: PinMarker = {
      id: (Date.now() + Math.random()).toString(),
      x: pos.x,
      y: pos.y,
      label: selectedLabel,
      color: DAMAGE_TYPES.find(t => t.label === selectedLabel)?.color || '#ef4444',
      aiPredictionIndex: placingForAI !== null ? placingForAI : undefined,
    };
    
    setPins([...pins, newPin]);
    setPinMode(false);
    setPlacingForAI(null);
  };

  const handleDeletePin = (id: string) => {
    setPins(pins.filter(p => p.id !== id));
  };

  const handlePlacePinForAI = (index: number) => {
    setPlacingForAI(index);
    setPinMode(true);
  };

  const drawPinMarkers = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imageLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw pin markers
    pins.forEach((pin, index) => {
      ctx.beginPath();
      ctx.arc(pin.x, pin.y, 20, 0, 2 * Math.PI);
      ctx.fillStyle = pin.color;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Draw number
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((index + 1).toString(), pin.x, pin.y);
    });
  };

  const handleAddToProposal = () => {
    if (frame) {
      onAddToProposal(frame, pins);
      toast.success("Frame added to proposal");
      onOpenChange(false);
    }
  };

  if (!frame) return null;

  const damageTypeCount = frame.analysis?.predictions.reduce((acc, pred) => {
    acc[pred.class] = (acc[pred.class] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Frame Analysis - {frame.timestamp.toFixed(1)}s</DialogTitle>
        </DialogHeader>

        <div className="flex gap-6 flex-1 overflow-hidden">
          {/* Left Sidebar - Manual Annotation Tools */}
          <div className="w-64 space-y-4 overflow-y-auto">
            <div className="space-y-2">
              <h3 className="font-semibold">Markup Tools</h3>
              <Select value={selectedLabel} onValueChange={setSelectedLabel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAMAGE_TYPES.map((type) => (
                    <SelectItem key={type.label} value={type.label}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => setPinMode(!pinMode)}
                variant={pinMode ? "default" : "outline"}
                size="sm"
                className="w-full"
              >
                {pinMode ? "📍 Tap to Place" : "📍 Place Pin"}
              </Button>
            </div>

            {/* AI Detected Damage */}
            {frame.analysis?.predictions && frame.analysis.predictions.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-sm">AI Detected</h3>
                <ScrollArea className="h-[150px]">
                  <div className="space-y-2">
                    {frame.analysis.predictions.map((pred: any, idx: number) => (
                      <div key={idx} className="text-sm p-2 bg-muted rounded-lg flex justify-between items-center">
                        <div className="flex-1">
                          <div className="font-medium">{pred.class}</div>
                          <div className="text-xs text-muted-foreground">
                            Confidence: {(pred.confidence * 100).toFixed(0)}%
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePlacePinForAI(idx)}
                          className="ml-2"
                        >
                          📍
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Placed Pins */}
            <div className="space-y-2">
              <h3 className="font-medium text-sm">Placed Pins</h3>
              <ScrollArea className="h-[150px]">
                <div className="space-y-2">
                  {pins.map((pin, idx) => (
                    <div key={pin.id} className="text-sm p-2 bg-muted rounded-lg flex justify-between items-center">
                      <div>
                        <div className="font-medium">#{idx + 1} {pin.label}</div>
                        {pin.aiPredictionIndex !== undefined && (
                          <div className="text-xs text-muted-foreground">
                            Linked to AI finding #{pin.aiPredictionIndex + 1}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePin(pin.id)}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Center - Image with canvas overlay */}
          <div className="flex-1 relative bg-muted/30 rounded-lg overflow-hidden flex items-center justify-center">
            <div className="relative" style={{ width: canvasWidth || 'auto', height: canvasHeight || 'auto' }}>
              <img
                ref={imgRef}
                src={frame.imageUrl}
                alt={`Frame at ${frame.timestamp}s`}
                className="max-w-full max-h-[70vh] object-contain"
                onLoad={handleImageLoad}
                crossOrigin="anonymous"
              />
              {canvasWidth > 0 && canvasHeight > 0 && (
                <canvas
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ 
                    pointerEvents: pinMode ? 'auto' : 'none',
                    cursor: pinMode ? 'crosshair' : 'default'
                  }}
                />
              )}
            </div>
          </div>

          {/* Right Sidebar - Analysis Details */}
          <div className="w-80 flex flex-col">
            <ScrollArea className="flex-1">
              <div className="space-y-6 pr-4">
                {/* Summary */}
                {frame.analysis?.summary && (
                  <div>
                    <h3 className="font-semibold mb-3">Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                        <span className="text-sm">Total Findings</span>
                        <Badge variant="default">{frame.analysis.summary.totalFindings}</Badge>
                      </div>
                      {frame.analysis.summary.hailCount > 0 && (
                        <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                          <span className="text-sm">Hail Damage</span>
                          <Badge variant="destructive">{frame.analysis.summary.hailCount}</Badge>
                        </div>
                      )}
                      {frame.analysis.summary.otherDamageCount > 0 && (
                        <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                          <span className="text-sm">Other Damage</span>
                          <Badge variant="secondary">{frame.analysis.summary.otherDamageCount}</Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Damage Type Breakdown */}
                {damageTypeCount && Object.keys(damageTypeCount).length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">Breakdown by Type</h3>
                    <div className="space-y-2">
                      {Object.entries(damageTypeCount).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between p-2 rounded bg-muted">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: DAMAGE_COLORS[type] || DAMAGE_COLORS["other"] }}
                            />
                            <span className="text-sm capitalize">{type.replace('-', ' ')}</span>
                          </div>
                          <Badge variant="outline">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Text Findings */}
                {frame.analysis?.findings && frame.analysis.findings.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">AI Observations</h3>
                    <ul className="space-y-2">
                      {frame.analysis.findings.map((finding, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex gap-2">
                          <span className="text-primary">•</span>
                          <span>{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {frame.status === 'error' && (
                  <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
                    {frame.error || 'Analysis failed'}
                  </div>
                )}
              </div>
            </ScrollArea>

            <Separator className="my-4" />

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={handleAddToProposal}
                disabled={!frame.analysis?.damageDetected && pins.length === 0}
              >
                Add to Proposal
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};